import {
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  UploadFile
} from 'antd'
import { UploadChangeParam } from 'antd/es/upload'
import dayjs, { Dayjs } from 'dayjs'

import { GetContractResponse } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CustomItemRender,
  InternalInstitutionsSelect,
  Upload
} from 'src/components'
import { useLocales } from 'src/locales'

import { FundingSourceSelect } from './FundingSourceSelect'

const { Item } = Form

type Props = {
  data?: GetContractResponse
  onChange?: (info: UploadChangeParam<UploadFile>) => void
}

export const OtherInstitutionsAddContractForm = ({ data, onChange }: Props) => {
  const { t } = useLocales()

  return (
    <Card>
      <Flex vertical gap={16}>
        <Form.Item shouldUpdate>
          {({ getFieldValue }) => {
            const isStartDateDisabled = !getFieldValue('signedOn')
            const isEndDateDisabled = !getFieldValue('startDate')

            const disabledStartDate = (current: Dayjs) => {
              return (
                current &&
                current < dayjs(getFieldValue('signedOn')).endOf('day')
              )
            }

            const disabledEndDate = (current: Dayjs) => {
              return (
                current &&
                current < dayjs(getFieldValue('startDate')).endOf('day')
              )
            }

            return (
              <Row gutter={[24, 24]}>
                <Item name="beneficiaryId" hidden />

                <Col xs={24} md={12} xl={8}>
                  <Item
                    name="beneficiaryId"
                    label={t('stock_applications.beneficiary')}
                    rules={requiredRules}
                  >
                    <InternalInstitutionsSelect
                      placeholder={t('counterparts.provider')}
                    />
                  </Item>
                </Col>

                <Col xs={24} md={12} xl={8}>
                  <Item
                    name="contractNumber"
                    label={t('counterparts.contract_number')}
                    rules={requiredRules}
                  >
                    <InputNumber
                      min={0}
                      placeholder={t('counterparts.contract_number')}
                      className="full-width"
                    />
                  </Item>
                </Col>

                <Col xs={24} md={12} xl={8}>
                  <Item
                    name="signedOn"
                    label={t('counterparts.signed_date')}
                    rules={requiredRules}
                  >
                    <DatePicker
                      className="full-width"
                      placeholder={t('counterparts.signed_date')}
                    />
                  </Item>
                </Col>

                <Col xs={24} md={8}>
                  <Item
                    name="startDate"
                    label={t('counterparts.contract_start_date')}
                    rules={requiredRules}
                  >
                    <DatePicker
                      disabled={isStartDateDisabled}
                      disabledDate={disabledStartDate}
                      className="full-width"
                      placeholder={t('counterparts.contract_start_date')}
                    />
                  </Item>
                </Col>

                <Col xs={24} md={8}>
                  <Item
                    name="endDate"
                    label={t('counterparts.contract_end_date')}
                    rules={requiredRules}
                  >
                    <DatePicker
                      disabled={isEndDateDisabled}
                      disabledDate={disabledEndDate}
                      className="full-width"
                      placeholder={t('counterparts.contract_end_date')}
                    />
                  </Item>
                </Col>

                <Col xs={24} md={8}>
                  <Item
                    name="fundingSourceId"
                    label={t('menu.funding_source')}
                    rules={requiredRules}
                  >
                    <FundingSourceSelect />
                  </Item>
                </Col>

                <Divider />

                <Item
                  name="docs"
                  className="full-width"
                  label={t('counterparts.additional_files')}
                  rules={requiredRules}
                >
                  <Upload
                    defaultFileList={
                      data?.files?.map((file) => ({
                        ...file,
                        uid: file.id,
                        name: file.name
                      })) as UploadFile[]
                    }
                    onChange={onChange}
                    customRequest={() => null}
                    itemRender={(_, file, __, { remove }) => (
                      <CustomItemRender file={file} onRemove={remove} />
                    )}
                  />
                </Item>

                <Divider />

                <Item
                  name="description"
                  className="full-width"
                  label={t('counterparts.description')}
                >
                  <Input.TextArea
                    rows={5}
                    placeholder={t('counterparts.description')}
                  />
                </Item>
              </Row>
            )
          }}
        </Form.Item>
      </Flex>
    </Card>
  )
}
